/*
Copyright (C) 2026  Tech9IQ
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/**
 * Swift's screens before a session, in signin.tech9iq.com's look (Customer-Login
 * apps/web/src/ui.tsx AuthLayout and styles.css): the dark Tech9IQ brand half
 * beside the form half, the same in every Tech9IQ app. Below 960px the brand
 * half steps aside and the logo sits above the card instead.
 */

import React from "react";
import styled, { keyframes } from "styled-components";

import { ThemePalette } from "@src/components/Theme";
import tech9iqLogo from "./images/tech9iq-logo.png";
// 2026-10-07 (logo pass): the sign-in app's dark variant (coloured lettering) for the dark panel and the dark theme.
import tech9iqLogoDark from "./images/tech9iq-logo-dark.png";

const LOGO_ALT = "Tech9IQ — your intelligent cloud";
const NARROW = "@media (max-width: 959px)";
const WIDE = "@media (min-width: 960px)";

const Split = styled.div`
  position: absolute;
  inset: 0;
  overflow: auto;
  display: grid;
  grid-template-columns: minmax(360px, 44%) 1fr;
  background: ${ThemePalette.grayscale[7]};
  font-family: Montserrat, ui-sans-serif, system-ui, "Segoe UI", sans-serif;
  ${NARROW} {
    display: block;
  }
`;
const Hero = styled.aside`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 40px;
  min-height: 100vh;
  box-sizing: border-box;
  padding: 40px 48px;
  color: #fff;
  background:
    radial-gradient(
      40rem 28rem at 110% -10%,
      rgba(70, 196, 242, 0.22),
      transparent 60%
    ),
    radial-gradient(
      34rem 26rem at -20% 110%,
      rgba(113, 193, 115, 0.18),
      transparent 60%
    ),
    linear-gradient(160deg, #212c33, #3d525f);
  /* A faint dot grid, so the panel reads as a surface rather than a flat fill. */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: 0.5;
    background-image: radial-gradient(
      rgba(255, 255, 255, 0.09) 1px,
      transparent 1px
    );
    background-size: 22px 22px;
  }
  & > * {
    position: relative;
  }
  ${NARROW} {
    display: none;
  }
`;
/* On a white plate, like the cloud tiles: the logo's slate lettering would disappear into the dark panel.
   2026-10-07 (logo pass): no plate; the sign-in app's dark variant sits on the panel. Was:
  padding: 10px 16px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18); */
const HeroBrand = styled.div`
  align-self: flex-start;
  img {
    display: block;
    height: 44px;
    width: auto;
  }
`;
const HeroBody = styled.div`
  max-width: 30rem;
  margin-block: auto;
`;
const HeroTitle = styled.h2`
  margin: 0 0 28px;
  font-size: 30px;
  line-height: 38px;
  font-weight: 600;
  text-wrap: balance;
  color: #fff;
`;
const HeroClouds = styled.ul`
  list-style: none;
  margin: 0 0 28px;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 20px;
  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    line-height: 20px;
    color: rgba(255, 255, 255, 0.92);
  }
`;
const HeroTile = styled.span`
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #fff;
  display: grid;
  place-items: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  svg {
    width: 22px;
    height: 22px;
  }
`;
const HeroPoints = styled.ul`
  list-style: none;
  margin: 0;
  padding: 20px 0 0;
  border-top: 1px solid rgba(255, 255, 255, 0.16);
  display: flex;
  flex-direction: column;
  gap: 10px;
  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 14px;
    line-height: 20px;
    color: rgba(255, 255, 255, 0.82);
  }
  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    margin-top: 3px;
    color: ${ThemePalette.success};
  }
`;
const Shell = styled.div`
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px 20px;
  background: ${ThemePalette.grayscale[7]};
`;
const Wrap = styled.div`
  width: 100%;
  max-width: 448px;
  margin-block: auto;
`;
const Brand = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
  img {
    height: 48px;
    width: auto;
    display: block;
  }
  /* 2026-10-07 (logo pass): the light logo, or the sign-in app's dark variant in the dark theme. */
  img.logo-dark {
    display: none;
  }
  /* Was ":where(:root[data-theme="dark"]) & img.logo-light / img.logo-dark": styled-components glues
     a selector that starts with ":" onto the component (".x:where(:root…) .x img"), so it never
     matched and the dark theme kept the light logo. html[data-theme] is the same switch. */
  html[data-theme="dark"] & img.logo-light {
    display: none;
  }
  html[data-theme="dark"] & img.logo-dark {
    display: block;
  }
  /* Beside the brand half the logo is already on screen: keep only the product name. */
  ${WIDE} {
    img,
    img.logo-light,
    img.logo-dark,
    .brand-divider {
      display: none;
    }
    /* The dark-theme rule above outranks the plain one, so it is hidden here by name too. */
    html[data-theme="dark"] & img.logo-dark {
      display: none;
    }
  }
`;
const BrandDivider = styled.span`
  height: 24px;
  width: 1px;
  background: ${ThemePalette.grayscale[2]};
`;
const BrandName = styled.span`
  font-size: 18px;
  line-height: 28px;
  font-weight: 600;
  color: ${ThemePalette.black};
`;
const Card = styled.section`
  box-sizing: border-box;
  /* Was #fff in both themes: in dark the card stayed white under the theme's light text. */
  background: var(--t9-bg-surface);
  border: 1px solid ${ThemePalette.grayscale[0]};
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.08);
`;
const Legal = styled.footer`
  margin: 32px auto 0;
  width: 100%;
  max-width: 640px;
  text-align: center;
  font-size: 11px;
  line-height: 16px;
  color: ${ThemePalette.grayscale[5]};
  p {
    margin: 0 0 8px;
  }
  nav {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px 16px;
  }
  a {
    color: ${ThemePalette.primary};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;
const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 20px;
`;
const HeadTitle = styled.h1`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: ${ThemePalette.black};
  text-wrap: balance;
`;
const HeadSub = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${ThemePalette.grayscale[5]};
`;

/** The clouds' marks, as Customer-Login's assets/cloud-icons.svg draws them. Inline: a sprite this small is bundled as a data URL, which <use> can't point into. */
const CloudMark = ({ name }: { name: string }) => {
  switch (name) {
    case "nutanix":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <g
            stroke="#7855FA"
            strokeWidth="2.6"
            fill="none"
            strokeLinecap="round"
          >
            <path d="M5 5l4.6 4.6" />
            <path d="M14.4 14.4L19 19" />
            <path d="M19 5l-4.6 4.6" />
            <path d="M9.6 14.4L5 19" />
          </g>
        </svg>
      );
    case "ibm":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <g fill="#0F62FE" stroke="none">
            {[
              [1, 5.5, 4.4],
              [2.2, 8.7, 2],
              [2.2, 11.9, 2],
              [2.2, 15.1, 2],
              [1, 18.3, 4.4],
              [6.4, 5.5, 5.4],
              [7.4, 8.7, 2],
              [10.4, 8.7, 2.2],
              [7.4, 11.9, 4.6],
              [7.4, 15.1, 2],
              [10.4, 15.1, 2.2],
              [6.4, 18.3, 5.4],
              [13.6, 5.5, 3.2],
              [19.8, 5.5, 3.2],
              [14.6, 8.7, 3.2],
              [18.8, 8.7, 3.2],
              [14.6, 11.9, 2.2],
              [17.55, 11.9, 1.5],
              [19.8, 11.9, 2.2],
              [14.6, 15.1, 2.2],
              [19.8, 15.1, 2.2],
              [13.6, 18.3, 3.2],
              [19.8, 18.3, 3.2],
            ].map(([x, y, w]) => (
              <rect key={`${x}-${y}`} x={x} y={y} width={w} height="2" />
            ))}
          </g>
        </svg>
      );
    case "aws":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <text
            x="12"
            y="12"
            textAnchor="middle"
            fontSize="10.5"
            fontWeight="700"
            fontFamily="Arial, Helvetica, sans-serif"
            letterSpacing="-.3"
            fill="#232F3E"
            stroke="none"
          >
            aws
          </text>
          <g
            stroke="#FF9900"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3.5 15.4c5.1 3.3 11.9 3.3 17 0" />
            <path d="M17.4 14.2l3.1 1.2-.9 3" />
          </g>
        </svg>
      );
    case "azure":
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <g fill="#0078D4" stroke="none">
            <path d="M10.2 4h4.4L8.2 20H3z" />
            <path d="M15.1 8l5.9 12H10.4l6.4-2-4.2-5.4z" />
          </g>
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <g
            stroke="#0F62FE"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M17.5 19H7a4.5 4.5 0 1 1 .9-8.9A6 6 0 0 1 19 11.5a3.75 3.75 0 0 1-1.5 7.5z" />
            <path d="M9.5 13.6h5" />
            <path d="M9.5 16.2h5" />
          </g>
        </svg>
      );
  }
};

/** The clouds one Tech9IQ sign-in reaches. */
const HERO_CLOUDS = [
  { mark: "nutanix", name: "Nutanix" },
  { mark: "ibm", name: "IBM Power (AIX | Linux)" },
  { mark: "aws", name: "AWS" },
  { mark: "azure", name: "Azure" },
  { mark: "ibm-cloud", name: "IBM Cloud" },
];

const HERO_POINTS = [
  "Sovereign, compliance-ready data centres",
  "Two-factor sign-in, isolated per organization",
  "24×7 managed operations",
];

/** lucide circle-check, as icons.svg#i-circle-check. */
const CircleCheck = () => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

/** The brand half: who this is, what one sign-in reaches, and why it's safe. Decoration only. */
const AuthHero = () => (
  <Hero aria-label="About Tech9IQ">
    <HeroBrand>
      {/* 2026-10-07 (logo pass) was: <img src={tech9iqLogo} alt={LOGO_ALT} /> on a white plate */}
      <img src={tech9iqLogoDark} alt={LOGO_ALT} />
    </HeroBrand>
    <HeroBody>
      <HeroTitle>One sign-in for every cloud your business runs on.</HeroTitle>
      <HeroClouds>
        {HERO_CLOUDS.map(cloud => (
          <li key={cloud.name}>
            <HeroTile>
              <CloudMark name={cloud.mark} />
            </HeroTile>
            {cloud.name}
          </li>
        ))}
      </HeroClouds>
      <HeroPoints>
        {HERO_POINTS.map(point => (
          <li key={point}>
            <CircleCheck />
            {point}
          </li>
        ))}
      </HeroPoints>
    </HeroBody>
  </Hero>
);

/** A card's heading: title and one line saying what the step is for (Customer-Login's CardHead). */
export const CardHead = (props: {
  title: React.ReactNode;
  sub?: React.ReactNode;
}) => (
  <Head>
    <HeadTitle>{props.title}</HeadTitle>
    {props.sub ? <HeadSub>{props.sub}</HeadSub> : null}
  </Head>
);

type Props = {
  children: React.ReactNode;
  /** Under the card, outside it (the server's disclaimer). */
  after?: React.ReactNode;
  /** A line above the legal links (Swift's open-source licence notice). */
  notice?: React.ReactNode;
};

/** The brand half beside the form half, with `children` in the white card. */
const AuthLayout = (props: Props) => (
  <Split data-test-id="authLayout">
    <AuthHero />
    <Shell>
      <Wrap>
        <Brand>
          {/* 2026-10-07 (logo pass) was: <img src={tech9iqLogo} alt={LOGO_ALT} /> */}
          <img className="logo-light" src={tech9iqLogo} alt={LOGO_ALT} />
          <img className="logo-dark" src={tech9iqLogoDark} alt={LOGO_ALT} />
          <BrandDivider className="brand-divider" />
          <BrandName>SwiftIQ</BrandName>
        </Brand>
        <Card aria-live="polite">{props.children}</Card>
        {props.after}
      </Wrap>
      <Legal>
        {props.notice ? <p>{props.notice}</p> : null}
        <nav aria-label="Legal and contact">
          <span>
            &copy; {new Date().getFullYear()} Integrated Tech9labs Pvt. Ltd.
          </span>
          {/* 2026-10-07: the legal pages every Tech9IQ sign-in links, the sign-in app's own. Were placeholders:
          <a href="#">Customer Agreement</a>
          <a href="#">Privacy Notice</a>
          <a href="#">Cookie Notice</a> */}
          <a href="https://signin.tech9iq.com/privacy">Privacy</a>
          <a href="https://signin.tech9iq.com/terms">Terms</a>
          <a href="https://signin.tech9iq.com/cookies">Cookies</a>
          <a href="https://signin.tech9iq.com/customer-engagement">Customer engagement</a>
          <a href="mailto:sales@tech9iq.com">Talk to sales</a>
        </nav>
      </Legal>
    </Shell>
  </Split>
);

/* ------------------------------------------------------------------------- */
/* The Tech9IQ preloader (public/index.html #t9-preload, the same in every Tech9IQ app), for a
   moment that has a page but nothing to show yet: the logo, a sliding bar and one line. */

const slide = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(250%); }
`;
const Preload = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 16px;
  background: ${ThemePalette.grayscale[7]};
  color: ${ThemePalette.grayscale[5]};
  font:
    500 14px/20px Montserrat,
    ui-sans-serif,
    system-ui,
    -apple-system,
    "Segoe UI",
    sans-serif;
  text-align: center;
  img {
    display: block;
    height: 40px;
    width: auto;
  }
  /* 2026-10-07 (logo pass): the sign-in app's dark variant in the dark theme. */
  img.logo-dark {
    display: none;
  }
  /* Was ":where(:root[data-theme="dark"]) & img.logo-light / img.logo-dark": styled-components glues
     a selector that starts with ":" onto the component (".x:where(:root…) .x img"), so it never
     matched and the dark theme kept the light logo. html[data-theme] is the same switch. */
  html[data-theme="dark"] & img.logo-light {
    display: none;
  }
  html[data-theme="dark"] & img.logo-dark {
    display: block;
  }
  p {
    margin: 0;
  }
  a {
    color: ${ThemePalette.primary};
  }
`;
const PreloadBar = styled.div`
  position: relative;
  width: 160px;
  height: 3px;
  overflow: hidden;
  border-radius: 9999px;
  background: ${ThemePalette.grayscale[0]};
  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 40%;
    border-radius: inherit;
    background: ${ThemePalette.primary};
    animation: ${slide} 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
      width: 100%;
      opacity: 0.45;
    }
  }
`;

/** If the page never gets ready, say so rather than spin for ever (as index.html does). */
const SLOW_MS = 20000;

export class AuthPreloader extends React.Component<
  { text: string },
  { slow: boolean }
> {
  state = { slow: false };

  timer: number | undefined;

  componentDidMount() {
    this.timer = window.setTimeout(
      () => this.setState({ slow: true }),
      SLOW_MS,
    );
  }

  componentWillUnmount() {
    window.clearTimeout(this.timer);
  }

  render() {
    return (
      <Preload role="status" aria-live="polite" data-test-id="authPreloader">
        {/* 2026-10-07 (logo pass) was: <img src={tech9iqLogo} alt="Tech9IQ" /> */}
        <img className="logo-light" src={tech9iqLogo} alt="Tech9IQ" />
        <img className="logo-dark" src={tech9iqLogoDark} alt="Tech9IQ" />
        <PreloadBar aria-hidden="true" />
        {this.state.slow ? (
          <p>
            This is taking longer than usual. <a href="">Reload</a>
          </p>
        ) : (
          <p>{this.props.text}</p>
        )}
      </Preload>
    );
  }
}

export default AuthLayout;
